import React, { useState, useEffect, useRef } from "react";

import SVGComponent from "../../SVGComponent";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";
import ICONS from "../../../../assets/Icons";

// Suggestions of props for component while using the components -------------------------------------

/**
 * TimelineAccordionCard Component
 *
 * @param {Object} props - Component properties
 *
 * @param {Object} props.account - Main timeline data object for the current row.
 *
 * @param {boolean} props.openApprovalTimeline - Controls whether the accordion is expanded or collapsed.
 *
 * @param {Function} props.handleApprovalTimeline - Callback fired when the card is clicked to toggle expand/collapse.
 *
 * @param {Function} props.renderApprovalData - Function that returns JSX for displaying the expanded approval details.
 *
 * @param {String} props.FirstDivHeaderText - Heading text for the first information box.
 *
 * @param {String} props.FirstDivSubtext - Subheading or description for the first information box.
 *
 * @param {boolean} props.SecondDivCondition - Controls visibility of the second div box.
 *
 * @param {String|boolean} props.SecondtDivImage - Image or icon shown inside the second div box (false if hidden).
 *
 * @param {String} props.SecondDivHeaderText - Heading text for the second div box.
 *
 * @param {String} props.SecondDivSubHeaderText - Subheading text for the second div box.
 *
 * @param {boolean} props.ThirdDivCondition - Controls visibility of the third div box.
 *
 * @param {String|boolean} props.ThirdDivImage - Image/icon for the third div box (false if not displayed).
 *
 * @param {String} props.ThirdDivHeaderText - Heading text for the third div box.
 *
 * @param {String} props.ThirdDivSubHeaderText - Subheading text for the third div box.
 *
 * @param {String} props.LastDivArrowIcon1 - Icon URL for collapsed arrow state.
 *
 * @param {String} props.LastDivArrowIcon2 - Icon URL for expanded arrow state.
 *
 * @param {String} props.imgColor - Optional dynamic color applied to images/icons.
 *
 * @param {boolean} props.FourthDivCondition - Controls visibility of the fourth div box.
 *
 * @param {boolean} props.borderRequired - Controls on if user required right border or not
 *
 * @param {String|boolean} props.FourthDivImage - Image/icon for the fourth div box.
 *
 * @param {String} props.FourthDivHeaderText - Heading text for the fourth div box.
 *
 * @param {String} props.FourthDivSubHeaderText - Subheading text for the fourth div box.
 */

const TimelineAccordionCard = ({
  account,
  openApprovalTimeline,
  handleApprovalTimeline,
  LastDivArrowIcon2,
  imgColor,
  isAnimationPopupOpen,
  setIsAnimationPopupOpen,
}) => {
  const excludedKeys = [
    "action",
    "timestamp",
    "last_used",
    "first_name",
    "last_name",
    "user_id",
    "user_full_name",
    "platform",
    "os_version",
    "manufacturer",
    "browser_name",
    "serial_number",
    "model",
    "sdk_version",
    "app_version_name",
    "app_version_code",
    "ip_address",
    "request_ip",
    "is_primary",
    "logged_in_time",
    "logged_in_flag",
    "is_verified",
    "final_approval",
    "authorization_rule",
    "rejection_reason",
    "remark",
  ];

  const dynamicFields = Object.entries(account || {}).filter(
    ([key, value]) =>
      !excludedKeys.includes(key) &&
      value !== null &&
      value !== undefined &&
      value !== "" &&
      value !== "unknown",
  );

  const containerRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(openApprovalTimeline);
  const [hasExpandedOnce, setHasExpandedOnce] = useState(openApprovalTimeline);
  const isOpen = openApprovalTimeline;

  const isUTCDate = (value) => {
    if (typeof value !== "string") return false;

    const date = new Date(value);

    return (
      !Number?.isNaN(date?.getTime()) &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value)
    );
  };

  const formatFieldValue = (value) => {
    if (isUTCDate(value)) {
      return formatDateTime(value);
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (value && typeof value === "object") {
      return JSON.stringify(value, null, GlobalConfiguration?.lengthCheckTwo);
    }

    return String(value);
  };

  useEffect(() => {
    const el = containerRef.current;

    if (openApprovalTimeline) {
      setHasExpandedOnce(true);
      setShouldRender(true);
      if (el) {
        el.style.maxHeight = "";
        el.style.removeProperty("--accordion-start-height");
      }
      return;
    }

    // Skip collapse timing on initial mount for closed cards
    if (!hasExpandedOnce || !el) return;

    // Measure real height so collapse animates immediately (no dead time
    // from an oversized max-height like 5000px).
    const fullHeight = el.scrollHeight;
    el.style.setProperty("--accordion-start-height", `${fullHeight}px`);
    el.style.maxHeight = "";

    // Fallback if animationend does not fire
    const timeout = setTimeout(() => {
      setShouldRender(false);
      el.style.removeProperty("--accordion-start-height");
      el.style.maxHeight = "";
    }, IntervalConfiguration?.intervalSmallMid);

    return () => clearTimeout(timeout);
  }, [openApprovalTimeline, hasExpandedOnce]);

  const handleAnimationEnd = (event) => {
    if (event.target !== containerRef.current) return;

    if (isOpen) {
      // After expand finishes, drop the animated max-height so tall
      // sections like "Other Detail" are never clipped.
      containerRef.current.style.maxHeight = "none";
      return;
    }

    setShouldRender(false);
    containerRef.current.style.maxHeight = "";
    containerRef.current.style.removeProperty("--accordion-start-height");
  };

  const isValid = (value) =>
    value !== null &&
    value !== undefined &&
    value.toString().trim() !== "" &&
    value.toString().toLowerCase() !== "unknown";

  const formatDateTime = (isoString) => {
    if (!isoString) return "";

    const date = new Date(isoString);

    const day = date.getDate();
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");

    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;

    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  };
  const hasUserDetails =
    isValid(account?.first_name) ||
    isValid(account?.last_name) ||
    isValid(account?.user_id) ||
    isValid(account?.user_full_name);

  const hasDeviceDetails =
    isValid(account?.platform) ||
    isValid(account?.os_version) ||
    isValid(account?.manufacturer) ||
    isValid(account?.browser_name) ||
    isValid(account?.serial_number) ||
    isValid(account?.model);

  const hasAppDetails =
    account?.platform === "android" || account?.platform === "iOS";

  const hasNetworkDetails =
    isValid(account?.ip_address) ||
    isValid(account?.request_ip) ||
    isValid(account?.is_primary);

  const hasSessionDetails =
    isValid(account?.logged_in_time) ||
    isValid(account?.logged_in_flag) ||
    isValid(account?.is_verified) ||
    isValid(account?.is_primary);

  const hasAuthorizationDetails =
    isValid(account?.final_approval) || isValid(account?.authorization_rule);

  const hasRejectionDetails =
    account?.action === "Rejected" && isValid(account?.rejection_reason);

  const hasRemarkDetails = isValid(account?.remark);
  const hasAdditionalDetails = dynamicFields?.length > 0;

  const hasAccordionContent =
    hasUserDetails ||
    hasDeviceDetails ||
    hasAppDetails ||
    hasNetworkDetails ||
    hasSessionDetails ||
    hasAuthorizationDetails ||
    hasRejectionDetails ||
    hasRemarkDetails ||
    hasAdditionalDetails;

  const animationClass = isOpen
    ? "expand-animation"
    : hasExpandedOnce
      ? "collapse-animation"
      : "";

  return (
    <>
      <div
        ref={containerRef}
        className={`flex flex-col w-width-full px-padding-md pt-padding-md pb-padding-sm Qsmd:pb-padding-base rounded-border-radius-md bg-primary-card z-z-index-base  relative  ${isOpen ? "" : "overflow-hidden"} origin-top ${animationClass}   ${hasAccordionContent ? "global-hover-white cursor-pointer" : ""}`}
        onAnimationEnd={handleAnimationEnd}
        onClick={() => {
          if (!hasAccordionContent) return;
          handleApprovalTimeline();
          setIsAnimationPopupOpen?.(!isAnimationPopupOpen);
        }}
      >
        <div className="flex flex-col gap-gap-md">
          <div className="flex flex-col justify-between Qsmd:flex-row  Qsmd:items-center">
            <div className="flex flex-row">
              <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                Activity Details
              </p>
              <img
                src={ICONS?.ACTIVITY_DETAILS}
                alt="Activity_details"
                className="w-icon-md h-icon-md "
              />
            </div>
            <div className="grid place-content-center absolute Qsmd:static right-right-base mr-margin-md Qsmd:mr-margin-base ">
              {hasAccordionContent && (
                <div
                  className={`transition-transform duration-animation-fast ease-in-out ${
                    openApprovalTimeline ? "-rotate-half" : "-rotate-base"
                  }`}
                >
                  <SVGComponent
                    color={imgColor}
                    height="var(--icon-xs)"
                    width="var(--icon-xs)"
                    url={LastDivArrowIcon2}
                    alt="Icon"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="grid Qsmd:grid-cols-3 grid-cols-1">
            {isValid(account?.action) && (
              <div className="mb-margin-sm Qsmd:mb-margin-md">
                <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs">
                  {account?.action}
                </p>
                <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                  Action
                </p>
              </div>
            )}
            {isValid(account?.timestamp) && (
              <div className="mb-margin-sm Qsmd:mb-margin-md">
                <p className="text-text-primary text-font-sm text-left font-sans font-normal Qsmd:mb-margin-xs">
                  {formatDateTime(account?.timestamp)}
                </p>
                <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                  Timestamp
                </p>
              </div>
            )}
            {isValid(account?.last_used) && (
              <div className="mb-margin-sm Qsmd:mb-margin-md">
                <p className="text-text-primary text-font-sm text-left font-sans font-normal Qsmd:mb-margin-xs">
                  {formatDateTime(account?.last_used)}
                </p>
                <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                  Last used
                </p>
              </div>
            )}
          </div>
        </div>

        {shouldRender && (
          <>
            {/* 1st */}
            {(isValid(account?.first_name) ||
              isValid(account?.last_name) ||
              isValid(account?.user_id) ||
              isValid(account?.user_full_name)) && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>
                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      User Details
                    </p>
                    <img
                      src={ICONS?.USER_DETAILS}
                      alt="User_details"
                      className="w-icon-md h-icon-md "
                    />
                  </div>

                  <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                    {isValid(account?.first_name) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md mr-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.first_name}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Name
                        </p>
                      </div>
                    )}
                    {isValid(account?.last_name) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.last_name}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Last name
                        </p>
                      </div>
                    )}
                    {isValid(account?.user_id) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.user_id}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          User ID
                        </p>
                      </div>
                    )}

                    {isValid(account?.user_full_name) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.user_full_name}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          User full name
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* 2nd */}
            {(isValid(account?.platform) ||
              isValid(account?.os_version) ||
              isValid(account?.manufacturer) ||
              isValid(account?.browser_name) ||
              isValid(account?.serial_number) ||
              isValid(account?.model)) && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>
                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      Platform/Device Details
                    </p>
                    <img
                      src={ICONS?.DEVICE_DETAILS}
                      alt="Device_details"
                      className="w-icon-md h-icon-md "
                    />
                  </div>

                  <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                    {isValid(account?.platform) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.platform}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Platform
                        </p>
                      </div>
                    )}

                    {isValid(account?.os_version) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.os_version}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          OS version
                        </p>
                      </div>
                    )}

                    {isValid(account?.manufacturer) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.manufacturer}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Manufacture
                        </p>
                      </div>
                    )}

                    {isValid(account?.browser_name) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.browser_name}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Browser
                        </p>
                      </div>
                    )}

                    {isValid(account?.serial_number) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.serial_number}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Serial number
                        </p>
                      </div>
                    )}

                    {isValid(account?.model) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.model}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Model
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* 3rd */}
            {(account?.platform === "android" ||
              account?.platform === "iOS") && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>

                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      App Details
                    </p>
                    <img
                      src={ICONS?.APP_DETAILS}
                      alt="App_details"
                      className="w-icon-md h-icon-md "
                    />
                  </div>

                  <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                    {isValid(account?.sdk_version) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs">
                          {account?.sdk_version}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          SDK version
                        </p>
                      </div>
                    )}

                    {isValid(account?.app_version_name) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.app_version_name}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          App version
                        </p>
                      </div>
                    )}

                    {isValid(account?.app_version_code) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.app_version_code}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          App code
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* 4th */}
            {(isValid(account?.ip_address) ||
              isValid(account?.request_ip) ||
              isValid(account?.is_primary)) && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>
                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      Network Details
                    </p>
                    <img
                      src={ICONS?.NETWORK_DETAILS}
                      alt="network_details"
                      className="w-icon-md h-icon-md "
                    />
                  </div>

                  <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                    {isValid(account?.ip_address) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm">
                          {account?.ip_address}
                        </p>
                        <p className="text-text-secondary text-font-xxs">
                          IP Address
                        </p>
                      </div>
                    )}

                    {isValid(account?.request_ip) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.request_ip}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Requested IP
                        </p>
                      </div>
                    )}

                    {isValid(account?.is_primary) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.is_primary ? "Yes" : "No"}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Primary
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* 5th */}
            {(isValid(account?.logged_in_time) ||
              isValid(account?.logged_in_flag) ||
              isValid(account?.is_primary) ||
              isValid(account?.is_verified)) && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>
                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      Session Details
                    </p>
                    <img
                      src={ICONS?.SESSION_DETAILS}
                      alt="Session_details"
                      className="w-icon-md h-icon-md "
                    />
                  </div>
                  <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                    {isValid(account?.logged_in_time) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal Qsmd:mb-margin-xs">
                          {formatDateTime(account?.logged_in_time)}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Logged in time
                        </p>
                      </div>
                    )}

                    {isValid(account?.logged_in_flag) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.logged_in_flag ? "Yes" : "No"}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Logged in
                        </p>
                      </div>
                    )}

                    {isValid(account?.is_verified) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.is_verified ? "Yes" : "No"}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Verified
                        </p>
                      </div>
                    )}

                    {isValid(account?.is_primary) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.is_primary ? "Yes" : "No"}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Primary
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* 6th */}
            {(isValid(account?.final_approval) ||
              isValid(account?.authorization_rule)) && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>

                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      Authorization
                    </p>
                    <img
                      src={ICONS?.AUTHORIZATION}
                      alt="Authorization"
                      className="w-icon-md h-icon-md "
                    />
                  </div>

                  <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                    {isValid(account?.final_approval) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs">
                          {account?.final_approval ? "Yes" : "No"}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Final approval
                        </p>
                      </div>
                    )}

                    {isValid(account?.authorization_rule) && (
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs break-words">
                          {account?.authorization_rule}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Authorization rule
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            {/* 7th */}
            {account?.action === "Rejected" && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>

                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      Rejection
                    </p>
                    <img
                      src={ICONS?.REJECTION}
                      alt="Rejection"
                      className="w-icon-md h-icon-md "
                    />
                  </div>
                  {isValid(account?.rejection_reason) && (
                    <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs">
                          {account?.rejection_reason}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Rejected reason
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* 8th */}
            {isValid(account?.remark) && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>

                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      Remark
                    </p>
                    <img
                      src={ICONS?.REMARK}
                      alt="Remark"
                      className="w-icon-md h-icon-md "
                    />
                  </div>
                  {isValid(account?.remark) && (
                    <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                      <div className="mb-margin-sm Qsmd:mb-margin-md">
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal capitalize Qsmd:mb-margin-xs">
                          {account?.remark}
                        </p>
                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal">
                          Remark
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            {dynamicFields?.length > 0 && (
              <>
                <div className="w-width-full border-b border-primary-bg mb-margin-md Qsmd:mt-margin-base mt-margin-sm"></div>

                <div className="flex flex-col gap-gap-md">
                  <div className="flex flex-row">
                    <p className="text-text-primary font-sans font-semibold text-font-md mr-margin-sm">
                      Other Detail
                    </p>

                    <img
                      src={ICONS?.ACTIVITY_DETAILS}
                      alt="Additional Details"
                      className="w-icon-md h-icon-md"
                    />
                  </div>

                  <div className="grid Qsmd:grid-cols-3 grid-cols-1">
                    {dynamicFields?.map(([key, value]) => (
                      <div
                        key={key}
                        className="mb-margin-sm Qsmd:mb-margin-md"
                      >
                        <p className="text-text-primary text-font-sm text-left font-sans font-normal break-words">
                          {formatFieldValue(value)}
                        </p>

                        <p className="text-text-secondary text-font-xxs text-left font-sans font-normal mt-margin-xs">
                          {key
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default TimelineAccordionCard;
